import { dataISOLocal } from '../../pedidos/precos'
import { useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { formatarMoeda } from '../../lib/formatos'
import { iniciaisDoNome } from '../../lib/iniciais'
import {
  cartoesPorFornecedora, mesInicial, mesAnterior, mesSeguinte, rotuloMes,
  type CartaoFornecedora,
} from '../../comissoes/conferencia'
import {
  seloDoMes, vendasDoMes, recebidaNoMes, variacao, metasDoMes, progressoDaMaior,
  contarAtingidas, corDaBarra, rotuloMesCurto, numerosDoResumo, destaqueDoCartao,
} from '../../comissoes/painelDoMes'
import { realizadoDaMeta } from '../../metas/metasDados'

// Spec 2026-10-04 (print dela "Dashboard de Comissoes"): a lista virou painel do
// mes. A conferencia e a baixa continuam na tela de dentro, sem mudanca.

const COR_DA_BARRA = { verde: 'bg-emerald-500', verde_claro: 'bg-emerald-400', laranja: 'bg-orange-400' }
const CORES_DO_CIRCULO = [
  'bg-teal-100 text-teal-800', 'bg-sky-100 text-sky-800', 'bg-violet-100 text-violet-800',
  'bg-rose-100 text-rose-800', 'bg-amber-100 text-amber-800', 'bg-lime-100 text-lime-800',
]
const corDoCirculo = (id: string) =>
  CORES_DO_CIRCULO[[...id].reduce((s, c) => s + c.charCodeAt(0), 0) % CORES_DO_CIRCULO.length]
const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`

function Barra({ pct, cor }: { pct: number; cor: string }) {
  return (
    <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
      <div className={`h-full rounded-full ${cor}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </div>
  )
}

function Quadro({ fundo, icone, rotulo, children }: { fundo: string; icone: string; rotulo: string; children: ReactNode }) {
  return (
    <div className={`rounded-xl p-3 space-y-1 min-w-0 ${fundo}`}>
      <p className="text-xs text-slate-600"><span aria-hidden="true">{icone}</span> {rotulo}</p>
      {children}
    </div>
  )
}

function Variacao({ rotulo, atual, anterior }: { rotulo: string; atual: number; anterior: number }) {
  const v = variacao(atual, anterior)
  const cor = v.direcao === 'alta' ? 'text-emerald-700' : v.direcao === 'queda' ? 'text-red-600' : 'text-slate-500'
  const seta = v.direcao === 'alta' ? '↑ ' : v.direcao === 'queda' ? '↓ ' : ''
  return (
    <div className="text-center min-w-0">
      <p className="text-[11px] text-slate-500">{rotulo}</p>
      <p className={`text-sm font-bold ${cor}`}>{seta}{v.texto}</p>
    </div>
  )
}

function SeloDaFornecedora({ cartao }: { cartao: CartaoFornecedora }) {
  const ref = cartao.refPendencia ? ` · ref. ${rotuloMesCurto(cartao.refPendencia)}` : ''
  const linhas = cartao.pendencias > 1 ? ` · ${cartao.pendencias} linhas` : ''
  const classe = 'inline-block mt-1 rounded-lg px-2 py-0.5 text-[11px] font-semibold'
  // O selo mostra o LIQUIDO; com mais de uma linha ele diz quantas ha por tras
  // (um credito de 1.000 e um debito de 1.000,01 nao viram "Debito R$ 0,01" mudo).
  if (cartao.saldo < 0) return <span className={`${classe} bg-red-50 text-red-700`}>⚠ Débito {formatarMoeda(-cartao.saldo)}{ref}{linhas}</span>
  if (cartao.saldo > 0) return <span className={`${classe} bg-sky-50 text-sky-700`}>↑ Crédito {formatarMoeda(cartao.saldo)}{ref}{linhas}</span>
  // Liquido zero com linhas em aberto: sem este selo nada levaria ate a aba
  // Debitos/Creditos, a unica saida desse par (a baixa em lote nao o alcanca).
  if (cartao.pendencias > 0) return <span className={`${classe} bg-amber-50 text-amber-800`}>⚠ Débito e crédito a acertar ({cartao.pendencias})</span>
  return <span className={`${classe} bg-emerald-50 text-emerald-700`}>✓ Em dia</span>
}

export default function Comissoes() {
  // Sem "voltar" proprio: o do cabecalho, que vive no Layout, ja cobre toda tela
  // funda. Tinha os dois, um embaixo do outro.
  const [mes, setMes] = useState<string | null>(null)
  // Decisao dela 04/10: o Resumo do mes filtra por 'geral' (pessoal) ou por uma
  // fornecedora. So o Resumo muda; a lista de baixo continua com todas.
  const [filtro, setFiltro] = useState('geral')
  // Congelado na primeira vez que os dados chegam, igual a tela de dentro. Se
  // ficasse derivado a cada render, dar baixa esvaziaria as liberadas e o filtro
  // pularia de mes sozinho -- numa tela de dinheiro isso se le como "nao gravou".
  const mesDeAbertura = useRef<string | null>(null)
  const lancamentos = useLiveQuery(
    () => banco.lancamentos_comissao.filter((l) => l.apagado_em === null).toArray(),
    [],
  )
  // Sem filtrar sub-pedido apagado, de proposito, e a tela de dentro faz igual:
  // lancamentos.ts:44-61 mantem vivo o lancamento JA RECEBIDO quando a venda cai
  // (nota cancelada, sub-pedido apagado), justamente para a divida com a fabrica
  // nao sumir da tela -- achado dela no pedido 2026-0014. Filtrar aqui desfaria
  // essa defesa e faria o dinheiro que ela DEVE desaparecer sem aviso.
  // (As vendas do mes filtram apagados por conta propria, em vendasDoMes.)
  const subPedidos = useLiveQuery(() => banco.sub_pedidos.toArray(), [])
  // Tambem sem filtrar apagadas, ao contrario das telas de LISTAGEM de cadastro
  // (ListaRepresentadas, ListaClientes...): aqui o assunto e dinheiro, e uma
  // fornecedora apagada com saldo em aberto precisa continuar cobravel. A tela
  // de dentro faz igual, entao os dois niveis concordam.
  const representadas = useLiveQuery(() => banco.representadas.toArray(), [])
  const pedidos = useLiveQuery(() => banco.pedidos.toArray(), [])
  // Spec 2026-10-05: a diferenca do mes entra no selo do cartao.
  const diferencas = useLiveQuery(() => banco.diferencas_comissao.toArray(), [])
  const metas = useLiveQuery(() => banco.metas.toArray(), [])
  // Realizado pelo MESMO calculo da tela Metas -- a barra daqui bate com a de la.
  // So das metas do mes aberto (revisao final 04/10: calcular todas as metas de
  // sempre, a cada gravacao, pesava no celular). O useLiveQuery ja refaz a conta
  // quando pedidos/lancamentos mudam, porque vigia as tabelas que a conta le.
  // Na abertura o mes ainda nao foi congelado: calcula pelo mesmo mesInicial.
  const realizados = useLiveQuery(async () => {
    const liberadasAgora = await banco.lancamentos_comissao
      .filter((l) => l.apagado_em === null && l.status === 'liberada').toArray()
    const mesAlvo = mes ?? mesDeAbertura.current ?? mesInicial(liberadasAgora, new Date())
    const doMesAlvo = metasDoMes(await banco.metas.toArray(), mesAlvo)
    const mapa = new Map<string, number>()
    for (const m of doMesAlvo) mapa.set(m.id, await realizadoDaMeta(m, new Date()))
    return mapa
  }, [mes])

  // useLiveQuery devolve undefined enquanto carrega.
  if (!lancamentos || !subPedidos || !representadas || !pedidos || !metas || !realizados || !diferencas) {
    return <p className="p-4 text-sm text-slate-500">Carregando…</p>
  }

  const liberadas = lancamentos.filter((l) => l.status === 'liberada')
  if (mesDeAbertura.current === null) {
    mesDeAbertura.current = mesInicial(liberadas, new Date())
  }
  const mesEscolhido = mes ?? mesDeAbertura.current
  const antes = mesAnterior(mesEscolhido)
  const cartoes = cartoesPorFornecedora(lancamentos, subPedidos, representadas, mesEscolhido, diferencas)
  const selo = seloDoMes(lancamentos, mesEscolhido, new Set(subPedidos.map((s) => s.id)), dataISOLocal(new Date()).slice(0, 7))
  const vendas = vendasDoMes(pedidos, subPedidos, mesEscolhido)
  const vendasAntes = vendasDoMes(pedidos, subPedidos, antes)
  const recebida = recebidaNoMes(lancamentos, subPedidos, mesEscolhido)
  const recebidaAntes = recebidaNoMes(lancamentos, subPedidos, antes)
  const doMes = metasDoMes(metas, mesEscolhido)
  const ehDeVenda = (tipo: string) => tipo === 'valor_vendido' || tipo === 'faturamento_total'
  const escopoDoFiltro = filtro === 'geral' ? 'pessoal' : `representada:${filtro}`
  const metasDoFiltro = doMes.filter((m) => m.escopo === escopoDoFiltro)
  // "Valor vendido" e "faturamento total" medem a mesma venda: vale a maior.
  const metaFaturamento = progressoDaMaior(metasDoFiltro.filter((m) => ehDeVenda(m.tipo)), realizados)
  const metaComissao = progressoDaMaior(metasDoFiltro.filter((m) => m.tipo === 'comissao_necessaria'), realizados)
  const atingidas = contarAtingidas(filtro === 'geral' ? doMes : metasDoFiltro, realizados)
  const metaDaFornecedora = (id: string) =>
    progressoDaMaior(doMes.filter((m) => m.escopo === `representada:${id}` && ehDeVenda(m.tipo)), realizados)
  // arredondar2 dentro de numerosDoResumo: todo dinheiro passa por ele.
  const numeros = numerosDoResumo(filtro, { vendas, vendasAntes, recebida, recebidaAntes, cartoes })

  // Spec 2026-10-04 §3: entra tambem quem VENDEU no mes e ainda nao tem comissao.
  const idsComCartao = new Set(cartoes.map((c) => c.representadaId))
  const soVenda: CartaoFornecedora[] = [...vendas.porRepresentada.keys()]
    .filter((id) => !idsComCartao.has(id))
    .map((id) => representadas.find((r) => r.id === id))
    .filter((r): r is NonNullable<typeof r> => r !== undefined)
    .map((r) => ({
      representadaId: r.id, nome: r.nome, aReceber: 0, quantidade: 0, aReceberTotal: 0,
      previstas: 0, saldo: 0, pendencias: 0, refPendencia: null,
    }))
  const lista = [...cartoes, ...soVenda]
    .sort((a, b) => b.aReceber - a.aReceber || a.nome.localeCompare(b.nome, 'pt-BR'))

  return (
    <section className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-800">Comissões</h2>
          <p className="text-xs text-slate-500">Acompanhe o desempenho de cada fornecedora.</p>
        </div>
        <span
          data-testid="selo-do-mes"
          className={`shrink-0 rounded-xl border px-2.5 py-1.5 text-xs font-semibold ${
            selo === 'fechado' ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : selo === 'aberto' ? 'bg-amber-50 text-amber-800 border-amber-200'
                : selo === 'andamento' ? 'bg-sky-50 text-sky-800 border-sky-200'
                : 'bg-slate-100 text-slate-600 border-slate-200'}`}
        >
          {selo === 'fechado' ? '🔒 Mês fechado' : selo === 'aberto' ? 'Em aberto' : selo === 'andamento' ? 'Em andamento' : 'Sem comissão'}
        </span>
      </div>

      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm p-2">
        <button aria-label="Mês anterior" onClick={() => setMes(mesAnterior(mesEscolhido))} className="px-3 py-1 text-xl text-slate-600">
          ‹
        </button>
        <span data-testid="mes-atual-lista" className="text-base font-bold text-slate-800">
          {rotuloMes(mesEscolhido)}
        </span>
        <button aria-label="Mês seguinte" onClick={() => setMes(mesSeguinte(mesEscolhido))} className="px-3 py-1 text-xl text-slate-600">
          ›
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-3 space-y-3">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-lg">📊</span>
          <div>
            <p className="font-bold text-slate-800">Resumo do mês</p>
            <p className="text-xs text-slate-500">{filtro === 'geral' ? 'Desempenho geral' : 'Desempenho da fornecedora'}</p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          Ver:
          <select
            data-testid="filtro-resumo" value={filtro} onChange={(e) => setFiltro(e.target.value)}
            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800"
          >
            <option value="geral">Geral (pessoal)</option>
            {lista.map((c) => <option key={c.representadaId} value={c.representadaId}>{c.nome}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <Quadro fundo="bg-sky-50" icone="🛒" rotulo="Pedidos">
            <p data-testid="quadro-pedidos" className="text-xl font-bold text-slate-800">{numeros.pedidos}</p>
          </Quadro>
          <Quadro fundo="bg-emerald-50" icone="📈" rotulo="Faturamento">
            <p data-testid="quadro-faturamento" className="text-base font-bold text-slate-800 break-words">{formatarMoeda(numeros.faturamento)}</p>
            {metaFaturamento && (
              <>
                <p className="text-[11px] text-slate-500">Meta: {formatarMoeda(metaFaturamento.alvo)}</p>
                <Barra pct={metaFaturamento.pct} cor={COR_DA_BARRA[corDaBarra(metaFaturamento.pct)]} />
                <p className="text-right text-xs font-bold text-emerald-800">{metaFaturamento.pct}%</p>
              </>
            )}
          </Quadro>
          <Quadro fundo="bg-amber-50" icone="💰" rotulo="A receber no mês">
            <p data-testid="total-topo-lista" className="text-base font-bold text-slate-800 break-words">{formatarMoeda(numeros.aReceber)}</p>
            <p data-testid="recebida-do-mes" className="text-[11px] text-slate-500">Recebida: {formatarMoeda(numeros.recebida)}</p>
            {metaComissao && (
              <>
                <Barra pct={metaComissao.pct} cor={COR_DA_BARRA[corDaBarra(metaComissao.pct)]} />
                <p className="text-right text-xs font-bold text-amber-800">{metaComissao.pct}%</p>
              </>
            )}
          </Quadro>
          <Quadro fundo="bg-violet-50" icone="🎯" rotulo="Metas atingidas">
            {atingidas.total === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma meta</p>
            ) : (
              <>
                <p className="text-xl font-bold text-slate-800">{atingidas.atingidas} de {atingidas.total}</p>
                <Barra pct={(atingidas.atingidas / atingidas.total) * 100} cor="bg-violet-500" />
                <p className="text-right text-xs font-bold text-violet-800">{Math.round((atingidas.atingidas / atingidas.total) * 100)}%</p>
              </>
            )}
          </Quadro>
        </div>
        <div className="border-t border-slate-100 pt-2">
          <p className="text-xs text-slate-500 mb-1"><span aria-hidden="true">📈</span> Comparado ao mês anterior</p>
          <div data-testid="comparacao" className="grid grid-cols-3 gap-1">
            <Variacao rotulo="Faturamento" atual={numeros.faturamento} anterior={numeros.faturamentoAntes} />
            <Variacao rotulo="Pedidos" atual={numeros.pedidos} anterior={numeros.pedidosAntes} />
            <Variacao rotulo="Comissão" atual={numeros.recebida} anterior={numeros.recebidaAntes} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <h3 className="font-bold text-slate-800">Por fornecedora</h3>
        <Link to="/metas" className="shrink-0 rounded-lg bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700">
          Ver metas por fornecedora ›
        </Link>
      </div>

      {lista.length === 0 && (
        <p className="text-sm text-slate-500">Nenhuma comissão registrada ainda.</p>
      )}

      <ul className="space-y-2">
        {lista.map((cartao) => {
          const venda = vendas.porRepresentada.get(cartao.representadaId) ?? { pedidos: 0, faturamento: 0 }
          const meta = metaDaFornecedora(cartao.representadaId)
          const recebidaDela = recebida.porRepresentada.get(cartao.representadaId) ?? 0
          const destaque = destaqueDoCartao(cartao.aReceber, recebidaDela)
          return (
            <li key={cartao.representadaId}>
              <Link
                to={`/comissoes/${cartao.representadaId}?mes=${mesEscolhido}`}
                data-testid="cartao-fornecedora"
                className="flex gap-3 bg-white rounded-2xl shadow-sm p-3"
              >
                <span aria-hidden="true" className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold ${corDoCirculo(cartao.representadaId)}`}>
                  {iniciaisDoNome(cartao.nome)}
                </span>
                <span className="min-w-0 flex-1 space-y-0.5">
                  <span className="block text-sm font-bold text-slate-800 truncate">{cartao.nome}</span>
                  <span className="block text-xs text-slate-600">🛒 {plural(venda.pedidos, 'pedido', 'pedidos')}</span>
                  <span className="block text-xs text-slate-600 truncate">{formatarMoeda(venda.faturamento)} faturados</span>
                  {meta && (
                    <span className="flex items-center gap-2">
                      <span className={`shrink-0 text-xs font-bold ${meta.pct >= 80 ? 'text-emerald-700' : 'text-orange-500'}`}>Meta {meta.pct}%</span>
                      <span className="flex-1"><Barra pct={meta.pct} cor={meta.pct >= 80 ? 'bg-emerald-500' : 'bg-orange-400'} /></span>
                    </span>
                  )}
                </span>
                <span className="shrink-0 max-w-[45%] text-right">
                  <span className="block text-[11px] text-emerald-700">{destaque.rotulo}</span>
                  <span data-testid="destaque-cartao" className="block text-sm font-bold text-slate-800">{formatarMoeda(destaque.valor)}</span>
                  {destaque.recebidaEmbaixo && (
                    <span className="block text-[11px] text-slate-500">Recebida {formatarMoeda(recebidaDela)}</span>
                  )}
                  {/* O retrato inteiro, que o filtro de mes tirou do numero de
                      cima: em agosto ela confere julho enquanto agosto acumula
                      (smoke: R$ 1.020 no cartao, R$ 400 na tela). */}
                  {cartao.aReceberTotal !== cartao.aReceber && (
                    <span className="block text-[11px] text-slate-400">{formatarMoeda(cartao.aReceberTotal)} no total</span>
                  )}
                  {cartao.previstas > 0 && (
                    <span className="block text-[11px] text-slate-400">Previstas {formatarMoeda(cartao.previstas)}</span>
                  )}
                  <SeloDaFornecedora cartao={cartao} />
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
